import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { retornaLogado } from '../shared/functions/functions';
import { isProxiedApiUrl } from './proxied-api-url';

@Injectable()
export class CpfInterceptor implements HttpInterceptor {
  // URLs sensíveis que não devem receber o parâmetro CPF
  private readonly SENSITIVE_URLS = [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/refresh',
    '/api/security',
    '/api/admin'
  ];

  // Validação de CPF usando regex
  private readonly CPF_REGEX = /^\d{11}$/;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Verifica se é uma requisição POST ou DELETE
    if (request.method === 'POST' || request.method === 'DELETE') {
      try {
        // Verifica se a URL não é sensível
        if (!isProxiedApiUrl(request.url) || this.isSensitiveUrl(request.url)) {
          return next.handle(request);
        }

        // Obtém as credenciais do usuário logado
        const credentials = retornaLogado();
        
        if (credentials && credentials.cpf) {
          // Valida o formato do CPF
          if (!this.isValidCpf(credentials.cpf)) {
            // Continua sem adicionar o CPF inválido
            return next.handle(request);
          }

          // Verifica se o CPF já está presente na requisição
          if (this.cpfAlreadyPresent(request)) {
            return next.handle(request);
          }

          // Clona a requisição e adiciona o parâmetro de log de CPF
          const modifiedRequest = request.clone({
            setParams: {
              CPF_LOG: credentials.cpf
            }
          });
          
          return next.handle(modifiedRequest);
        } else {
          // Credenciais ou CPF não disponíveis
        }
              } catch (error) {
          // Se houver erro ao obter as credenciais, continua com a requisição original
          // Silenciosamente ignora o erro para não afetar o funcionamento
        }
    }
    
    // Para requisições que não são POST/DELETE, ou se não conseguir obter o CPF, continua normalmente
    return next.handle(request);
  }

  /**
   * Verifica se a URL é considerada sensível
   */
  private isSensitiveUrl(url: string): boolean {
    return this.SENSITIVE_URLS.some(sensitiveUrl => 
      url.includes(sensitiveUrl)
    );
  }

  /**
   * Valida se o CPF tem o formato correto (11 dígitos numéricos)
   */
  private isValidCpf(cpf: string): boolean {
    if (!cpf || typeof cpf !== 'string') {
      return false;
    }
    
    // Remove caracteres não numéricos
    const cleanCpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (!this.CPF_REGEX.test(cleanCpf)) {
      return false;
    }

    // Verifica se não é uma sequência de números iguais
    if (/^(\d)\1{10}$/.test(cleanCpf)) {
      return false;
    }

    // Validação do algoritmo de CPF (dígitos verificadores)
    return this.validateCpfAlgorithm(cleanCpf);
  }

  /**
   * Implementa o algoritmo de validação de CPF
   */
  private validateCpfAlgorithm(cpf: string): boolean {
    let sum = 0;
    let remainder;

    // Primeiro dígito verificador
    for (let i = 1; i <= 9; i++) {
      sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10))) return false;

    // Segundo dígito verificador
    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(10, 11))) return false;

    return true;
  }

  /**
   * Verifica se o CPF já está presente na requisição
   */
  private cpfAlreadyPresent(request: HttpRequest<unknown>): boolean {
    const params = request.params;
    return params.has('CPF_LOG') || params.has('cpf_log');
  }

  /**
   * Mascara o CPF para logs seguros (mostra apenas os primeiros e últimos dígitos)
   */
  private maskCpf(cpf: string): string {
    if (!cpf || cpf.length < 11) {
      return '***.***.***-**';
    }
    return `${cpf.substring(0, 3)}.***.***-${cpf.substring(9, 11)}`;
  }
} 